import { Injectable, signal } from '@angular/core';
import { catchError, forkJoin, of } from 'rxjs';
import { SidebarMenu } from '../_models/menu/SidebarMenu.model';
import { CompaniesService } from '../companies/services/companies.service';
import { SidebarService } from '../components/app-sidebar-minimizer/sidebar.service';
import { PortalService } from '../portale/_services/portal.service';
import { COMPANY_MENU_SHORTCUTS, CompanyMenuShortcut, findCompanyShortcut } from './company-menu-shortcuts';
import { ConfigService } from './config.service';
import { alberoEditorANodi, collegamentoVisibile, costruisciAlberoEditor, FunzioneRuolo, haSottogruppi, LIVELLO_SOTTOMENU, MenuEdMenu, MenuEdNodo, MenuEdVoce, nuovoUid, ruoliDellePagine, urlNelleSchede } from './menu-editor-model';
import { MENU_HUBS, normMenuUrl } from './menu-hub';
import { CompanyMenuOrgStore, fileFormatToNodes, flattenMenuLeaves, MenuOrgNode, MenuStileSottomenu, newMenuOrgId, nodesToFileFormat, parseMenuOrgStore } from './menu-organization';
import { MenuLayoutService } from './menu-layout.service';
import { RfiModeService } from './rfi-mode.service';

/** Riga della mappa del menu (vista ad albero stampabile). Titoli come chiavi i18n o testo. */
export interface MenuEdRigaMappa {
	titolo: string;
	tipo: 'menu' | 'sottomenu' | 'pagina' | 'collegamento';
	icona: string;
	url: string;
	/** Sezione a schede che toglie la voce dal menu (vuoto se resta nel menu) */
	scheda: string;
	figli: MenuEdRigaMappa[];
}

/** Punto dell'albero: dentro un menu (uid) oppure sulla barra (null), all'indice dato. */
export interface MenuEdPosizione {
	menu: string | null;
	index: number;
}

/**
 * Modifica del menu direttamente sul menu del portale (matita nell'header, solo ADMIN di
 * portale e solo desktop). Tiene la bozza finche' non si salva: il salvataggio scrive il
 * menu scelto tra quelli dell'azienda (profili di cmp_menu_org; col menu standard un menu
 * nuovo e attivo) con le impostazioni (posizione, stile dei gruppi, voci a capo).
 * Il menu e' quello dell'azienda attiva.
 */
@Injectable({ providedIn: 'root' })
export class MenuEditService {
	readonly attivo = signal(false);
	readonly caricamento = signal(false);
	readonly salvataggio = signal(false);
	/** Salvataggio con pagine non raggiungibili dal menu: la barra degli strumenti chiede conferma */
	readonly confermaRaggiungibili = signal(false);
	/** Uscita chiesta con modifiche non salvate: la barra degli strumenti chiede conferma */
	readonly confermaUscita = signal(false);

	albero: MenuEdNodo[] = [];
	modificato = false;
	/** Il profilo aveva gruppi oltre il terzo livello: nell'editor sono stati appiattiti nel sottogruppo che li contiene */
	appiattito = false;

	// ---- impostazioni del menu dell'azienda (si applicano con Salva) ----
	/** Menu dell'azienda che si sta modificando (null = nuovo, nasce col Salva) */
	profiloId: string | null = null;
	nomeProfilo = '';
	/** Il menu che si modifica e' quello che il portale usa per l'azienda */
	attivoPerAzienda = false;
	/** Come si aprono i gruppi del menu in alto: a tendina o come barra di Word (cmp_menu_org.stileSottomenu) */
	stile: MenuStileSottomenu = 'tendina';
	/** Menu in alto: le voci che non entrano vanno a capo invece che in "Altro" (cmp_menu_org.menuACapo) */
	aCapo = false;
	/** Seconda barra come Word: le voci che non entrano vanno a capo invece di scorrere (cmp_menu_org.barraACapo) */
	barraACapo = false;
	/** Seconda barra: sottogruppi a blocchi col nome sotto invece della tendina in hover (cmp_menu_org.barraSottogruppi) */
	barraBlocchi = false;
	/** Posizione del menu (cmp_menu_layout: left | top | top-center): in modifica si vede subito, si salva con Salva */
	layout = 'left';
	private layoutIniziale = 'left';
	/** Pannello "Impostazioni menu" aperto (nel servizio: sopravvive al passaggio barra in alto / sidebar) */
	pannelloImpostazioni = false;
	/** Pagine tolte dal menu (profilo.hidden): restano raggiungibili da link e card, si rimettono dalla palette */
	nascoste = new Set<number>();
	/** Menu salvati dell'azienda (profili di cmp_menu_org) */
	store: CompanyMenuOrgStore = { version: 2, activeId: null, profiles: [] };

	/** Ruolo dell'anteprima "Vedi come" (null = tutti) */
	ruolo: number | null = null;
	ruoli: { rl_id: number; rl_name: string }[] = [];
	/** L'anteprima per ruolo non e' disponibile (endpoint non raggiungibile) */
	senzaRuoli = false;

	private raw: SidebarMenu[] = [];
	private pagine = new Map<number, SidebarMenu>();
	private ruoliPagina = new Map<number, Set<number>>();
	private funzioniRuolo = new Map<number, FunzioneRuolo[]>();
	private schede = new Map<string, string>();
	private rfi = false;
	private cmpId = 0;
	private storia: string[] = [];
	private sidebarAperta = false;

	constructor(
		private portalService: PortalService,
		private companiesService: CompaniesService,
		private configService: ConfigService,
		private rfiMode: RfiModeService,
		private sidebarService: SidebarService,
		private menuLayoutService: MenuLayoutService,
	) {}

	/** La matita compare solo agli ADMIN di portale (vedono tutte le pagine dell'azienda con i loro ruoli). */
	puoModificare(): boolean {
		return this.configService.isCurrentUserAdmin() && this.configService.getCompany_id() > 0;
	}

	avvia() {
		if (this.attivo() || this.caricamento()) return;
		const prt = this.configService.getPortalID();
		const cmp = this.configService.getCompany_id();
		this.caricamento.set(true);
		forkJoin([
			this.portalService.getMenuItemsRaw(prt, cmp),
			this.companiesService.getMenuOrg(cmp),
			this.portalService.getMenuRolePreview(cmp).pipe(catchError(() => of(null))),
			this.rfiMode.attivo(cmp).pipe(catchError(() => of(false))),
			this.companiesService.getMenuLayout(cmp).pipe(catchError(() => of(this.menuLayoutService.layout))),
		]).subscribe({
			next: ([raw, rawStore, anteprima, rfi, layout]) => {
				this.cmpId = cmp;
				this.raw = (raw as any) || [];
				this.store = parseMenuOrgStore(rawStore) || { version: 2, activeId: null, profiles: [] };
				this.stile = this.store.stileSottomenu || 'tendina';
				this.aCapo = !!this.store.menuACapo;
				this.barraACapo = !!this.store.barraACapo;
				this.barraBlocchi = this.store.barraSottogruppi === 'blocchi';
				this.layout = this.layoutIniziale = layout === 'top' || layout === 'top-center' ? layout : 'left';
				this.pagine = new Map(flattenMenuLeaves(this.raw).map((p) => [p.pg_id, p]));
				this.ruoliPagina = ruoliDellePagine(this.raw);
				this.schede = urlNelleSchede(this.store.hubs, cmp);
				this.rfi = !!rfi;
				this.ruoli = anteprima?.roles || [];
				this.senzaRuoli = !anteprima;
				this.funzioniRuolo = new Map();
				for (const f of anteprima?.functions || []) {
					const lista = this.funzioniRuolo.get(f.rl_id) || [];
					lista.push(f);
					this.funzioniRuolo.set(f.rl_id, lista);
				}
				this.ruolo = null;
				this.apriMenuAttivo();
				this.apriSidebarSeServe();
				this.caricamento.set(false);
				this.attivo.set(true);
			},
			error: () => {
				this.caricamento.set(false);
				this.configService.openErrorNotify('Impossibile caricare il menu da modificare');
			},
		});
	}

	/** Si parte dal menu attivo; col menu standard la bozza diventa un menu nuovo e attivo. */
	private apriMenuAttivo() {
		const attivo = this.store.activeId ? this.store.profiles.find((p) => p.id === this.store.activeId) : undefined;
		if (attivo) {
			this.caricaProfilo(attivo.id);
		} else {
			this.caricaAlbero(null);
			this.profiloId = null;
			this.nomeProfilo = this.nomeNuovoProfilo();
			this.attivoPerAzienda = true;
		}
	}

	private caricaAlbero(nodes: MenuOrgNode[] | null, nascoste: number[] = []) {
		this.nascoste = new Set(nascoste.filter((id) => this.pagine.has(id)));
		const { albero, appiattito } = costruisciAlberoEditor(this.raw, nodes, [...this.nascoste]);
		this.albero = albero;
		this.appiattito = appiattito;
		this.storia = [];
		this.modificato = false;
	}

	private caricaProfilo(id: string) {
		const p = this.store.profiles.find((x) => x.id === id);
		if (!p) return;
		this.caricaAlbero(p.nodes, p.hidden || []);
		this.profiloId = p.id;
		this.nomeProfilo = p.name;
		this.attivoPerAzienda = this.store.activeId === p.id;
	}

	/** Con il menu a sinistra si modifica nella sidebar: se e' ridotta a icone la si apre (e si richiude uscendo). */
	private apriSidebarSeServe() {
		if (this.layout === 'left' && this.sidebarService.minimized) {
			this.sidebarService.toggleMinimized();
			this.sidebarAperta = true;
		}
	}

	// ---- impostazioni ----

	/** Apre un altro menu salvato dell'azienda (solo senza modifiche in sospeso). */
	apriProfilo(id: string) {
		if (this.modificato || id === this.profiloId) return;
		this.caricaProfilo(id);
	}

	/** Menu nuovo, partendo dal menu standard: nasce col Salva (non attivo finche' non lo si attiva). */
	nuovoProfilo() {
		this.caricaAlbero(null);
		this.profiloId = null;
		this.nomeProfilo = this.nomeNuovoProfilo();
		this.attivoPerAzienda = false;
		this.modificato = true;
	}

	/**
	 * Elimina subito dal server il menu aperto (solo un menu salvato e senza modifiche in sospeso). Se era quello attivo
	 * il portale torna al menu standard; l'editor riapre il menu attivo, come all'avvio.
	 */
	eliminaProfilo() {
		const id = this.profiloId;
		if (!id || this.modificato || this.salvataggio()) return;
		this.salvataggio.set(true);
		const errore = () => {
			this.salvataggio.set(false);
			this.configService.openErrorNotify('Errore eliminazione menu');
		};
		this.companiesService.getMenuOrg(this.cmpId).subscribe({
			next: (raw) => {
				const store: CompanyMenuOrgStore = parseMenuOrgStore(raw) || { version: 2, activeId: null, profiles: [] };
				const eraAttivo = store.activeId === id;
				store.profiles = store.profiles.filter((p) => p.id !== id);
				if (eraAttivo) store.activeId = null;
				this.companiesService.setMenuOrg(this.cmpId, JSON.stringify(store)).subscribe({
					next: () => {
						this.salvataggio.set(false);
						this.store = store;
						this.apriMenuAttivo();
						this.configService.openSuccessNotify(eraAttivo ? 'Menu eliminato: il portale usa il menu standard' : 'Menu eliminato');
						if (eraAttivo && this.cmpId === this.configService.getCompany_id()) {
							this.portalService.invalidateMenuCache();
							this.portalService.menuChanged$.next();
						}
					},
					error: errore,
				});
			},
			error: errore,
		});
	}

	rinominaProfilo(nome: string) {
		this.nomeProfilo = nome;
		this.modificato = true;
	}

	impostaAttivo(attivo: boolean) {
		this.attivoPerAzienda = attivo;
		this.modificato = true;
	}

	impostaACapo(aCapo: boolean) {
		this.aCapo = aCapo;
		this.modificato = true;
	}

	impostaBarraACapo(aCapo: boolean) {
		this.barraACapo = aCapo;
		this.modificato = true;
	}

	impostaBarraBlocchi(blocchi: boolean) {
		this.barraBlocchi = blocchi;
		this.modificato = true;
	}

	/** Posizione del menu: si vede subito (l'editor passa tra barra in alto e sidebar), si salva con Salva. */
	impostaLayout(layout: string) {
		if (layout === this.layout) return;
		this.layout = layout;
		this.modificato = true;
		this.menuLayoutService.apply(layout);
		this.apriSidebarSeServe();
	}

	/** File del menu (voci per NOME pagina, portabile tra portali e ambienti), come l'organizzazione menu. */
	esporta() {
		const hidden = [...this.nascoste].map((id) => this.pagine.get(id)?.name).filter((n): n is string => !!n);
		const payload = { type: 'kcs-menu-org', version: 2, name: this.nomeProfilo, nodes: nodesToFileFormat(alberoEditorANodi(this.albero), this.raw), hidden };
		const blob = new Blob([JSON.stringify(payload, null, '\t')], { type: 'application/json' });
		const a = document.createElement('a');
		a.href = URL.createObjectURL(blob);
		a.download = 'menu-' + (this.nomeProfilo || 'menu').replace(/[^\w\-]+/g, '_').toLowerCase() + '.json';
		a.click();
		URL.revokeObjectURL(a.href);
	}

	/** Importa un file di menu come menu nuovo (le pagine dell'azienda che il file non nomina vanno al loro posto standard). */
	importa(file: File) {
		file.text().then((testo) => {
			let parsed: any = null;
			try {
				parsed = JSON.parse(testo);
			} catch {
				/* gestito sotto */
			}
			if (!parsed || !Array.isArray(parsed.nodes)) {
				this.configService.openErrorNotify('File di menu non valido');
				return;
			}
			// pagine tolte dal menu nel file: per nome pagina, come le voci
			const perNome = new Map<string, number>([...this.pagine.values()].map((p) => [p.name, p.pg_id]));
			const hidden = (Array.isArray(parsed.hidden) ? parsed.hidden : []).map((n: any) => perNome.get(String(n))).filter((id: any): id is number => typeof id === 'number');
			this.caricaAlbero(fileFormatToNodes(parsed.nodes, this.raw), hidden);
			this.profiloId = null;
			this.nomeProfilo = (parsed.name ? String(parsed.name) : file.name.replace(/\.json$/i, '')) + ' (import)';
			this.attivoPerAzienda = false;
			this.modificato = true;
			this.configService.openSuccessNotify('Menu importato: controllalo e premi Salva');
		});
	}

	/** Matita accesa o "Esci": con modifiche non salvate la prima volta chiede conferma, la seconda esce. */
	chiediUscita() {
		if (this.modificato && !this.confermaUscita()) {
			this.confermaUscita.set(true);
			return;
		}
		this.esci();
	}

	esci() {
		this.confermaUscita.set(false);
		this.confermaRaggiungibili.set(false);
		this.pannelloImpostazioni = false;
		this.attivo.set(false);
		this.albero = [];
		// posizione provata e non salvata: torna com'era
		if (this.layout !== this.layoutIniziale) {
			this.layout = this.layoutIniziale;
			this.menuLayoutService.apply(this.layoutIniziale);
		}
		if (this.sidebarAperta && !this.sidebarService.minimized) this.sidebarService.toggleMinimized();
		this.sidebarAperta = false;
	}

	/** Salva; con pagine che il menu non fa piu' raggiungere chiede prima conferma (`forza` = salva comunque). */
	salva(forza = false) {
		if (this.salvataggio()) return;
		if (!forza && this.pagineIrraggiungibili().length) {
			this.confermaUscita.set(false);
			this.confermaRaggiungibili.set(true);
			return;
		}
		this.confermaRaggiungibili.set(false);
		this.salvataggio.set(true);
		const nodes = alberoEditorANodi(this.albero);
		// lo store com'e' ora sul server: profili e sezioni a schede possono essere cambiati altrove
		this.companiesService.getMenuOrg(this.cmpId).subscribe({
			next: (raw) => {
				const store: CompanyMenuOrgStore = parseMenuOrgStore(raw) || { version: 2, activeId: null, profiles: [] };
				if (this.stile === 'barra') store.stileSottomenu = 'barra';
				else delete store.stileSottomenu;
				if (this.aCapo) store.menuACapo = true;
				else delete store.menuACapo;
				if (this.barraACapo) store.barraACapo = true;
				else delete store.barraACapo;
				if (this.barraBlocchi) store.barraSottogruppi = 'blocchi';
				else delete store.barraSottogruppi;
				const nome = (this.nomeProfilo || '').trim() || this.nomeNuovoProfilo();
				let profilo = this.profiloId ? store.profiles.find((p) => p.id === this.profiloId) : undefined;
				if (profilo) {
					profilo.nodes = nodes;
					profilo.name = nome;
				} else {
					profilo = { id: newMenuOrgId('p'), name: nome, nodes };
					store.profiles.push(profilo);
				}
				if (this.nascoste.size) profilo.hidden = [...this.nascoste];
				else delete profilo.hidden;
				if (this.attivoPerAzienda) store.activeId = profilo.id;
				else if (store.activeId === profilo.id) store.activeId = null;
				const fine = () => {
					this.salvataggio.set(false);
					this.layoutIniziale = this.layout;
					this.configService.openSuccessNotify(this.attivoPerAzienda ? 'Menu salvato' : 'Menu salvato (non attivo: il portale usa un altro menu)');
					this.esci();
					if (this.cmpId === this.configService.getCompany_id()) {
						this.portalService.invalidateMenuCache();
						this.portalService.menuChanged$.next();
					}
				};
				this.companiesService.setMenuOrg(this.cmpId, JSON.stringify(store)).subscribe({
					next: () => {
						if (this.layout === this.layoutIniziale) return fine();
						this.companiesService.setMenuLayout(this.cmpId, this.layout).subscribe({ next: fine, error: () => this.erroreSalvataggio() });
					},
					error: () => this.erroreSalvataggio(),
				});
			},
			error: () => this.erroreSalvataggio(),
		});
	}

	private erroreSalvataggio() {
		this.salvataggio.set(false);
		this.configService.openErrorNotify('Errore nel salvataggio del menu');
	}

	private nomeNuovoProfilo(): string {
		const nomi = new Set(this.store.profiles.map((p) => p.name));
		let nome = 'Menu personalizzato';
		for (let i = 2; nomi.has(nome); i++) nome = `Menu personalizzato ${i}`;
		return nome;
	}

	// ---- lettura ----

	/** Azienda del menu in modifica (per gli indirizzi dei collegamenti con l'azienda dentro). */
	get cmpAttiva(): number {
		return this.cmpId;
	}

	/** HelpDesk RFI attivo per l'azienda: senza, i collegamenti solo RFI non si offrono. */
	get rfiAttivo(): boolean {
		return this.rfi;
	}

	pagina(v: MenuEdVoce): SidebarMenu | undefined {
		return v.pg_id != null ? this.pagine.get(v.pg_id) : undefined;
	}

	collegamento(v: MenuEdVoce): CompanyMenuShortcut | undefined {
		return v.key ? findCompanyShortcut(v.key) : undefined;
	}

	titolo(n: MenuEdNodo): string {
		if (n.tipo === 'menu') return n.label;
		if (n.tipo === 'pagina') return n.label || this.pagina(n)?.pg_title || '';
		return this.collegamento(n)?.labelKey || n.key || '';
	}

	icona(n: MenuEdNodo): string {
		if (n.tipo === 'menu') return n.icon;
		return n.icon || this.iconaOriginale(n);
	}

	/** Icona della pagina o della card (quella che la voce ha senza un'icona scelta nel menu). */
	iconaOriginale(n: MenuEdNodo): string {
		if (n.tipo === 'menu') return 'pi pi-folder';
		if (n.tipo === 'pagina') return this.pagina(n)?.styleclass || 'pi pi-file';
		return this.collegamento(n)?.icon || 'pi pi-link';
	}

	/** Indirizzo della pagina o del collegamento (come nel portale, con l'azienda al posto di :cmp). */
	urlDi(v: MenuEdVoce): string {
		if (v.tipo === 'pagina') return this.pagina(v)?.url || '';
		return this.collegamento(v)?.url(this.cmpId) || '';
	}

	/** Etichetta della sezione a schede che toglie questa voce dal menu (vuoto se resta nel menu). */
	inScheda(n: MenuEdNodo): string {
		return n.tipo === 'menu' ? '' : this.schede.get(normMenuUrl(this.urlDi(n))) || '';
	}

	/** Con un ruolo scelto in "Vedi come": la voce (o il menu, se nessuna sua voce) non e' visibile a quel ruolo. */
	nascostaAlRuolo(n: MenuEdNodo): boolean {
		if (this.ruolo == null) return false;
		if (n.tipo === 'menu') return !n.voci.some((v) => !this.nascostaAlRuolo(v));
		if (n.tipo === 'pagina') return !this.ruoliPagina.get(n.pg_id!)?.has(this.ruolo);
		const ruolo = this.ruolo;
		const puoAprire = (url: string) => [...this.pagine.values()].some((p) => normMenuUrl(p.url) === url && !!this.ruoliPagina.get(p.pg_id)?.has(ruolo));
		return !collegamentoVisibile(this.collegamento(n), this.funzioniRuolo.get(ruolo) || [], this.rfi, puoAprire);
	}

	/** Nome del ruolo di "Vedi come" (vuoto = tutti i ruoli). */
	nomeRuolo(): string {
		return this.ruoli.find((r) => r.rl_id === this.ruolo)?.rl_name || '';
	}

	/**
	 * Mappa del menu com'e' nella bozza, vista dal ruolo di "Vedi come": solo le voci che quel
	 * ruolo vede; i menu e i sottomenu senza voci visibili spariscono, come nel portale.
	 */
	mappa(): MenuEdRigaMappa[] {
		const visita = (lista: MenuEdNodo[], livello: number): MenuEdRigaMappa[] =>
			lista
				.filter((n) => !this.nascostaAlRuolo(n))
				.map((n): MenuEdRigaMappa => {
					if (n.tipo === 'menu') return { titolo: n.label, tipo: livello === 0 ? 'menu' : 'sottomenu', icona: n.icon, url: '', scheda: '', figli: visita(n.voci, livello + 1) };
					return { titolo: this.titolo(n), tipo: n.tipo, icona: this.icona(n), url: this.urlDi(n), scheda: this.inScheda(n), figli: [] };
				})
				.filter((r) => r.figli.length || (r.tipo !== 'menu' && r.tipo !== 'sottomenu'));
		return visita(this.albero, 0);
	}

	/** Collegamenti gia' nel menu (ciascuno compare una volta sola). */
	collegamentiUsati(): Set<string> {
		const usati = new Set<string>();
		const visita = (lista: MenuEdNodo[]) =>
			lista.forEach((n) => {
				if (n.tipo === 'menu') visita(n.voci);
				else if (n.tipo === 'collegamento' && n.key) usati.add(n.key);
			});
		visita(this.albero);
		return usati;
	}

	/**
	 * Dove sta un nodo: il gruppo che lo contiene (null = barra), l'indice e il livello di
	 * quel contenitore (0 barra, 1 menu, 2 sottomenu).
	 */
	trova(uid: string): { nodo: MenuEdNodo; menu: MenuEdMenu | null; index: number; livello: number } | null {
		const cerca = (lista: MenuEdNodo[], menu: MenuEdMenu | null, livello: number): ReturnType<MenuEditService['trova']> => {
			for (let i = 0; i < lista.length; i++) {
				const n = lista[i];
				if (n.uid === uid) return { nodo: n, menu, index: i, livello };
				if (n.tipo === 'menu') {
					const dentro = cerca(n.voci, n, livello + 1);
					if (dentro) return dentro;
				}
			}
			return null;
		};
		return cerca(this.albero, null, 0);
	}

	/** Livello di un contenitore: 0 la barra (null), 1 un menu, 2 un sottomenu; -1 se non esiste. */
	livelloDi(menuUid: string | null): number {
		if (menuUid == null) return 0;
		const da = this.trova(menuUid);
		return da?.nodo.tipo === 'menu' ? da.livello + 1 : -1;
	}

	private lista(menuUid: string | null): MenuEdNodo[] | null {
		if (menuUid == null) return this.albero;
		const da = this.trova(menuUid);
		return da?.nodo.tipo === 'menu' ? da.nodo.voci : null;
	}

	/**
	 * Regole dei livelli: le voci stanno ovunque; un gruppo sta sulla barra (menu) o dentro un
	 * menu (sottomenu, solo se non ha a sua volta sottomenu); nel sottomenu solo voci.
	 * `nodo` = 'gruppo' per un gruppo nuovo.
	 */
	puoStare(nodo: MenuEdNodo | 'gruppo', menuUid: string | null): boolean {
		const livello = this.livelloDi(menuUid);
		if (livello < 0) return false;
		if (nodo !== 'gruppo' && nodo.tipo !== 'menu') return true;
		if (livello >= LIVELLO_SOTTOMENU) return false;
		if (nodo === 'gruppo') return true;
		if (livello === 1 && haSottogruppi(nodo)) return false;
		// un gruppo non entra in se stesso
		return menuUid == null || (menuUid !== nodo.uid && !this.contiene(nodo, menuUid));
	}

	private contiene(gruppo: MenuEdMenu, uid: string): boolean {
		return gruppo.voci.some((v) => v.uid === uid || (v.tipo === 'menu' && this.contiene(v, uid)));
	}

	/** Pagine e collegamenti del menu con il percorso dei gruppi che li contengono (per "Inserisci pagina"). */
	elencoVoci(): { uid: string; nodo: MenuEdVoce; percorso: string[] }[] {
		const out: { uid: string; nodo: MenuEdVoce; percorso: string[] }[] = [];
		const visita = (lista: MenuEdNodo[], percorso: string[]) =>
			lista.forEach((n) => {
				if (n.tipo === 'menu') visita(n.voci, [...percorso, n.label]);
				else out.push({ uid: n.uid, nodo: n, percorso });
			});
		visita(this.albero, []);
		return out;
	}

	// ---- modifiche (ognuna annullabile) ----

	private ricorda() {
		this.storia.push(JSON.stringify({ albero: this.albero, nascoste: [...this.nascoste] }));
		if (this.storia.length > 50) this.storia.shift();
		this.modificato = true;
	}

	puoAnnullare(): boolean {
		return this.storia.length > 0;
	}

	annullaUltima() {
		const prima = this.storia.pop();
		if (prima) {
			const stato = JSON.parse(prima);
			this.albero = stato.albero;
			this.nascoste = new Set(stato.nascoste);
			this.modificato = this.storia.length > 0;
		}
	}

	/** Sposta una voce o un gruppo, rispettando i livelli (un menu portato dentro un menu diventa sottomenu). */
	sposta(uid: string, dest: MenuEdPosizione): boolean {
		const da = this.trova(uid);
		const lista = this.lista(dest.menu);
		if (!da || !lista || !this.puoStare(da.nodo, dest.menu)) return false;
		const origine = da.menu ? da.menu.voci : this.albero;
		let index = dest.index;
		if (origine === lista && da.index < index) index--;
		if (origine === lista && da.index === index) return false;
		this.ricorda();
		origine.splice(da.index, 1);
		lista.splice(Math.max(0, Math.min(index, lista.length)), 0, da.nodo);
		return true;
	}

	aggiungiCollegamento(key: string, dest: MenuEdPosizione): boolean {
		const lista = this.lista(dest.menu);
		if (!lista || this.collegamentiUsati().has(key) || !findCompanyShortcut(key)) return false;
		this.ricorda();
		lista.splice(Math.max(0, Math.min(dest.index, lista.length)), 0, { uid: nuovoUid(), tipo: 'collegamento', key });
		return true;
	}

	/** Stile dei gruppi (tendina o barra come Word): si salva con Salva. */
	cambiaStile(stile: MenuStileSottomenu) {
		if (stile === this.stile) return;
		this.stile = stile;
		this.modificato = true;
	}

	/** Gruppo nuovo e vuoto: sulla barra e' un gruppo, dentro un gruppo un sottogruppo. */
	nuovoGruppo(dest: MenuEdPosizione): MenuEdMenu | null {
		const lista = this.lista(dest.menu);
		if (!lista || !this.puoStare('gruppo', dest.menu)) return null;
		this.ricorda();
		const label = dest.menu == null ? 'Nuovo gruppo' : 'Nuovo sottogruppo';
		const gruppo: MenuEdMenu = { uid: nuovoUid(), tipo: 'menu', catId: newMenuOrgId('c'), label, icon: 'pi pi-folder', voci: [] };
		lista.splice(Math.max(0, Math.min(dest.index, lista.length)), 0, gruppo);
		return gruppo;
	}

	/**
	 * Toglie una pagina dal menu: non sparisce dal portale (link, card e permessi invariati), va tra le
	 * "Pagine nascoste" da cui si rimette. Le pagine non si eliminano: dipendono da servizi e ruoli.
	 */
	nascondi(uid: string): boolean {
		const da = this.trova(uid);
		if (!da || da.nodo.tipo !== 'pagina') return false;
		this.ricorda();
		(da.menu ? da.menu.voci : this.albero).splice(da.index, 1);
		this.nascoste.add(da.nodo.pg_id!);
		return true;
	}

	/** Rimette nel menu una pagina nascosta, nel punto dato. */
	ripristina(pg_id: number, dest: MenuEdPosizione): boolean {
		const lista = this.lista(dest.menu);
		if (!lista || !this.nascoste.has(pg_id)) return false;
		this.ricorda();
		this.nascoste.delete(pg_id);
		lista.splice(Math.max(0, Math.min(dest.index, lista.length)), 0, { uid: nuovoUid(), tipo: 'pagina', pg_id });
		return true;
	}

	/**
	 * Pagine fuori dal menu e se si raggiungono comunque: `via` dice da dove (un collegamento con lo stesso
	 * indirizzo, o la barra delle schede di una sezione attiva che nel menu ha la sua pagina, il cruscotto o
	 * un'altra scheda); vuoto = la pagina non si raggiunge dal menu ma l'azienda la ha (servizi e ruoli).
	 */
	statoNascoste(): { pg_id: number; titolo: string; icona: string; url: string; via: string; ruoli: string[]; perse: CompanyMenuShortcut[] }[] {
		const inMenu = new Set<string>();
		const visita = (lista: MenuEdNodo[]) => lista.forEach((n) => (n.tipo === 'menu' ? visita(n.voci) : inMenu.add(normMenuUrl(this.urlDi(n)))));
		visita(this.albero);
		const attive = new Set(this.store.hubs || []);
		const nomiRuolo = new Map(this.ruoli.map((r) => [r.rl_id, r.rl_name]));
		return this.pagineNascoste().map((p) => {
			const u = normMenuUrl(p.url);
			let via = inMenu.has(u) ? 'collegamento con lo stesso indirizzo' : '';
			for (const h of MENU_HUBS) {
				if (via || !attive.has(h.key)) continue;
				const sezione = [h.parentUrl || '', h.homeUrl, ...h.tabs.flatMap((t) => t.urls)].filter(Boolean).map((x) => normMenuUrl(x.replace(/:cmp/g, String(this.cmpId))));
				if (sezione.includes(u) && sezione.some((x) => x !== u && inMenu.has(x))) via = 'schede di ' + h.label;
			}
			const ruoli = [...(this.ruoliPagina.get(p.pg_id) || [])].map((id) => nomiRuolo.get(id) || '').filter(Boolean);
			return { ...p, via, ruoli, perse: via ? [] : this.collegamentiDellaPagina(u, inMenu) };
		});
	}

	/**
	 * Collegamenti che si aprono dalle card di una pagina di sezione (Fatture: Fatture attive, Registro fatture emesse,
	 * Da abbinare...) e che nel menu non ci sono: con la pagina fuori dal menu non si aprono piu' da nessuna voce.
	 */
	private collegamentiDellaPagina(url: string, inMenu: Set<string>): CompanyMenuShortcut[] {
		const cmp = (x: string) => normMenuUrl(x.replace(/:cmp/g, String(this.cmpId)));
		const sezione = new Set(MENU_HUBS.filter((h) => [h.parentUrl, h.homeUrl, h.ancoraUrl].some((x) => x && normMenuUrl(x) === url)).flatMap((h) => h.tabs.flatMap((t) => t.urls.map(cmp))));
		const visti = new Set<string>();
		return COMPANY_MENU_SHORTCUTS.filter((sc) => {
			const u = normMenuUrl(sc.url(this.cmpId));
			if ((sc.soloRfi && !this.rfi) || !sezione.has(u) || inMenu.has(u) || visti.has(u)) return false;
			visti.add(u);
			return true;
		});
	}

	/** Pagine dell'azienda che con questo menu non si raggiungono piu' (ne' dal menu ne' da collegamenti o schede). */
	pagineIrraggiungibili() {
		return this.statoNascoste().filter((p) => !p.via);
	}

	/** Pagine tolte dal menu, in ordine di nome (per la palette e per "Inserisci pagina"). */
	pagineNascoste(): { pg_id: number; titolo: string; icona: string; url: string }[] {
		return [...this.nascoste]
			.map((id) => this.pagine.get(id))
			.filter((p): p is SidebarMenu => !!p)
			.map((p) => ({ pg_id: p.pg_id, titolo: p.pg_title, icona: p.styleclass || 'pi pi-file', url: p.url || '' }));
	}

	/** Elimina un gruppo con tutto il suo contenuto: le pagine vanno tra le nascoste, i collegamenti si tolgono. */
	eliminaConVoci(uid: string): boolean {
		const da = this.trova(uid);
		if (!da || da.nodo.tipo !== 'menu') return false;
		this.ricorda();
		const pagine = (n: MenuEdNodo): number[] => (n.tipo === 'menu' ? n.voci.flatMap(pagine) : n.tipo === 'pagina' ? [n.pg_id!] : []);
		pagine(da.nodo).forEach((id) => this.nascoste.add(id));
		(da.menu ? da.menu.voci : this.albero).splice(da.index, 1);
		return true;
	}

	/** Toglie un collegamento; un gruppo eliminato lascia le sue voci al suo posto. Le pagine si nascondono (nascondi). */
	rimuovi(uid: string): boolean {
		const da = this.trova(uid);
		if (!da || da.nodo.tipo === 'pagina') return false;
		this.ricorda();
		const lista = da.menu ? da.menu.voci : this.albero;
		if (da.nodo.tipo === 'menu') {
			lista.splice(da.index, 1, ...da.nodo.voci);
		} else {
			lista.splice(da.index, 1);
		}
		return true;
	}

	/** Nome di un menu o di una pagina (vuoto su una pagina = torna al titolo della pagina). */
	rinomina(uid: string, nome: string) {
		const da = this.trova(uid);
		const testo = (nome || '').trim();
		if (!da || da.nodo.tipo === 'collegamento') return;
		if (da.nodo.tipo === 'menu') {
			if (!testo || testo === da.nodo.label) return;
			this.ricorda();
			da.nodo.label = testo;
			return;
		}
		const pagina = this.pagina(da.nodo);
		const label = testo && testo !== pagina?.pg_title ? testo : undefined;
		if (label === da.nodo.label) return;
		this.ricorda();
		if (label) da.nodo.label = label;
		else delete da.nodo.label;
	}

	/** Icona di qualsiasi voce: gruppo, sottogruppo, pagina o collegamento (sull'icona originale la voce torna senza). */
	cambiaIcona(uid: string, icon: string) {
		const da = this.trova(uid);
		if (!da || this.icona(da.nodo) === icon) return;
		this.ricorda();
		if (da.nodo.tipo === 'menu') {
			da.nodo.icon = icon;
		} else if (icon === this.iconaOriginale(da.nodo)) {
			delete da.nodo.icon;
		} else {
			da.nodo.icon = icon;
		}
	}
}
